import React, { useEffect, useMemo, useRef, useState } from 'react'
import { claimDescription, claimChipModel, claimUsage } from './claims'

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

export const ClaimChips = ({ claimsSupported, mappings, links, onShowMappings, onShowRule, onAddMapping, canEdit }) => {
  const usage = useMemo(() => claimUsage(mappings, links), [mappings, links])
  const chips = useMemo(() => claimChipModel(claimsSupported, usage), [claimsSupported, usage])
  const [open, setOpen] = useState(null)
  const openRef = useRef(null)

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') setOpen(null) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Outside click closes the open popover. Only attached while a popover is
  // open, and scoped to that chip's wrapper (button + popover), so clicking
  // inside the popover doesn't close it and clicking another chip's button
  // closes this one via mousedown and opens the new one via its own click.
  useEffect(() => {
    if (!open) return undefined
    const onMouseDown = e => {
      if (openRef.current && !openRef.current.contains(e.target)) setOpen(null)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])

  return (
    <div>
      <div className="sw-legend" aria-label="legend">
        <span><i className="sw-sw identity" />Identifies the person</span>
        <span><i className="sw-sw used" />Used by a mapping or rule (count)</span>
        <span><i className="sw-sw" />Available, not used</span>
        <span><i className="sw-sw unadvertised" />Used, but not advertised by the issuer</span>
      </div>
      <div className="sw-chips">
        {chips.map(chip => {
          const used = Object.prototype.hasOwnProperty.call(usage, chip.claim) ? usage[chip.claim] : undefined
          const isOpen = open === chip.claim
          return (
            <span key={chip.claim} className="position-relative" ref={isOpen ? openRef : null}>
              <button type="button" className={`sw-chip ${chip.kind}`} aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : chip.claim)}>
                {chip.claim}{chip.count > 0 && <span className="sw-count">{chip.count}</span>}
              </button>
              {isOpen &&
                <div className="sw-pop" role="dialog" aria-label={chip.claim} style={{ left: 0, top: 'calc(100% + 8px)' }}>
                  <h3>{chip.claim}</h3>
                  <p className="small sw-hint mb-0">{claimDescription(chip.claim)}</p>
                  {chip.kind === 'unadvertised' && <p className="small mb-0" style={{ color: 'var(--sw-warn)' }}>Not advertised by the issuer. Make sure the app registration is set to send it.</p>}
                  <div className="small d-grid gap-1">
                    <strong>Used by</strong>
                    {chip.kind === 'identity' && <span>Identifies the person at sign-in</span>}
                    {used && used.mappings > 0 && <span>{plural(used.mappings, 'role mapping')}</span>}
                    {used && used.rules.map(org => <span key={org}>Rule for {org}</span>)}
                    {chip.kind === 'available' && <span className="sw-hint">Nothing yet.</span>}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {used && used.mappings > 0 && <button type="button" className="btn sw-btn btn-sm" onClick={() => { setOpen(null); onShowMappings(chip.claim) }}>Show mappings</button>}
                    {used && used.rules.length > 0 && <button type="button" className="btn sw-btn btn-sm" onClick={() => { setOpen(null); onShowRule(chip.claim) }}>Show rule</button>}
                    {canEdit && chip.kind !== 'identity' && <button type="button" className="btn sw-btn-primary btn-sm" onClick={() => { setOpen(null); onAddMapping(chip.claim) }}>Add role mapping</button>}
                  </div>
                </div>}
            </span>
          )
        })}
      </div>
    </div>
  )
}
